import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Loader2, Lock, Play, Save, Trash2, Pencil, Plus, X, BarChart3,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { format, parseISO, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuth } from '@/hooks/useAuth';
import {
  useRunReport,
  useSavedReports,
  useCreateSavedReport,
  useUpdateSavedReport,
  useDeleteSavedReport,
  useActivityLog,
} from '@/hooks/api/useRetailReports';
import {
  ReportDimension,
  ReportMeasure,
  RetailActivityType,
  UserRole,
  type ReportRunRequest,
  type ReportRunResponse,
  type SavedReportDefinition,
} from '@pms/shared';
import type { ActivityLogRow } from '@/services/api/retailReports.api';

const REPORT_ROLES = new Set<UserRole>([
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
  UserRole.REVENUE_MANAGER,
]);

const DIMENSIONS: { key: ReportDimension; label: string }[] = [
  { key: ReportDimension.CATEGORY, label: 'Category' },
  { key: ReportDimension.PRODUCT, label: 'Product' },
  { key: ReportDimension.SKU, label: 'SKU' },
  { key: ReportDimension.DAY, label: 'Day' },
  { key: ReportDimension.SETTLEMENT_TYPE, label: 'Settlement type' },
  { key: ReportDimension.CASHIER, label: 'Cashier' },
];

const MEASURES: { key: ReportMeasure; label: string }[] = [
  { key: ReportMeasure.QTY, label: 'Qty' },
  { key: ReportMeasure.GROSS, label: 'Gross' },
  { key: ReportMeasure.TAX, label: 'Tax' },
  { key: ReportMeasure.NET, label: 'Net' },
  { key: ReportMeasure.DISCOUNT, label: 'Discount' },
];

const MONEY_MEASURES = new Set<ReportMeasure>([
  ReportMeasure.GROSS,
  ReportMeasure.TAX,
  ReportMeasure.NET,
  ReportMeasure.DISCOUNT,
]);

const DIM_LABEL = Object.fromEntries(DIMENSIONS.map((d) => [d.key, d.label])) as Record<string, string>;
const MEASURE_LABEL = Object.fromEntries(MEASURES.map((m) => [m.key, m.label])) as Record<string, string>;

const TABS = [
  { param: 'builder', label: 'Builder' },
  { param: 'saved', label: 'Saved' },
  { param: 'log', label: 'Activity Log' },
];

const ACTIVITY_LABELS: Record<RetailActivityType, string> = {
  [RetailActivityType.SALE]: 'Sale',
  [RetailActivityType.PAID_OUT]: 'Paid out',
  [RetailActivityType.STOCK_RECEIPT]: 'Stock receipt',
  [RetailActivityType.CONFIG_CHANGE]: 'Config change',
};

const PAGE_SIZE = 25;

function todayStr() { return format(new Date(), 'yyyy-MM-dd'); }
function defaultFrom() { return format(subDays(new Date(), 30), 'yyyy-MM-dd'); }

function fmtTs(iso: string): string {
  try { return format(parseISO(iso), 'MMM d, yyyy h:mm a'); } catch { return iso; }
}

// ─── Builder config shape (UI-owned state) ───────────────────────────────────

interface BuilderConfig {
  dimensions: ReportDimension[];
  measures: ReportMeasure[];
  dateFrom: string;
  dateTo: string;
  filters: { key: string; value: string }[];
}

function emptyConfig(): BuilderConfig {
  return { dimensions: [], measures: [], dateFrom: defaultFrom(), dateTo: todayStr(), filters: [] };
}

function configFromSaved(def: SavedReportDefinition): BuilderConfig {
  const c = (def.config ?? {}) as Partial<ReportRunRequest>;
  return {
    dimensions: Array.isArray(c.dimensions) ? c.dimensions : [],
    measures: Array.isArray(c.measures) ? c.measures : [],
    dateFrom: c.dateFrom ?? defaultFrom(),
    dateTo: c.dateTo ?? todayStr(),
    filters: Array.isArray(c.filters) ? c.filters.map((f) => ({ key: f.key, value: f.value })) : [],
  };
}

function toRunRequest(c: BuilderConfig): ReportRunRequest {
  return {
    dimensions: c.dimensions,
    measures: c.measures,
    dateFrom: c.dateFrom,
    dateTo: c.dateTo,
    ...(c.filters.length ? { filters: c.filters.filter((f) => f.key && f.value) } : {}),
  };
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function RetailReportsPage() {
  const propertyId = useSelectedPropertyId();
  const { user } = useAuth();
  const canView = !!user && REPORT_ROLES.has(user.role);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeParam = searchParams.get('tab') ?? 'builder';

  // Builder config is page-level so Saved → load-into-builder survives a tab switch.
  const [config, setConfig] = useState<BuilderConfig>(emptyConfig);

  function loadIntoBuilder(def: SavedReportDefinition) {
    setConfig(configFromSaved(def));
    setSearchParams({ tab: 'builder' });
  }

  if (user && !canView) {
    return (
      <div>
        <PageHeader title="Reports & Logs" description="POS reporting and activity" />
        <EmptyState icon={Lock} title="No access" description="You don't have access to reports." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Reports & Logs" description="Dynamic POS report builder and activity log" />

      {!propertyId ? (
        <EmptyState icon={BarChart3} title="Select a property" description="Select a property to continue." />
      ) : (
        <>
          <div className="mb-6 border-b border-gray-200">
            <nav className="-mb-px flex gap-6" aria-label="Report sections">
              {TABS.map((t) => (
                <button
                  key={t.param}
                  type="button"
                  onClick={() => setSearchParams({ tab: t.param })}
                  className={cn(
                    'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium',
                    t.param === activeParam
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  )}
                  aria-current={t.param === activeParam ? 'page' : undefined}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          {activeParam === 'builder' && (
            <ReportBuilderTab propertyId={propertyId} config={config} setConfig={setConfig} />
          )}
          {activeParam === 'saved' && (
            <SavedReportsTab propertyId={propertyId} onLoad={loadIntoBuilder} />
          )}
          {activeParam === 'log' && <ActivityLogTab propertyId={propertyId} />}
        </>
      )}
    </div>
  );
}

// ─── Chip group ──────────────────────────────────────────────────────────────

function Chips<T extends string>({
  label, options, selected, onToggle,
}: {
  label: string;
  options: { key: T; label: string }[];
  selected: T[];
  onToggle: (key: T) => void;
}) {
  return (
    <div>
      <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = selected.includes(o.key);
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onToggle(o.key)}
              aria-pressed={on}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border',
                on
                  ? 'bg-primary-100 text-primary-700 border-primary-200'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
              )}
            >
              {o.label}
              {on && <X size={12} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Builder tab ─────────────────────────────────────────────────────────────

function ReportBuilderTab({
  propertyId, config, setConfig,
}: {
  propertyId: string;
  config: BuilderConfig;
  setConfig: React.Dispatch<React.SetStateAction<BuilderConfig>>;
}) {
  const runMutation = useRunReport();
  const [result, setResult] = useState<ReportRunResponse | null>(null);
  const [lastRunOk, setLastRunOk] = useState(false);
  const [showSave, setShowSave] = useState(false);

  const canRun = config.dimensions.length >= 1 && config.measures.length >= 1;

  function toggleDimension(key: ReportDimension) {
    setConfig((c) => ({
      ...c,
      dimensions: c.dimensions.includes(key) ? c.dimensions.filter((d) => d !== key) : [...c.dimensions, key],
    }));
    setLastRunOk(false);
  }
  function toggleMeasure(key: ReportMeasure) {
    setConfig((c) => ({
      ...c,
      measures: c.measures.includes(key) ? c.measures.filter((m) => m !== key) : [...c.measures, key],
    }));
    setLastRunOk(false);
  }
  function addFilter() {
    setConfig((c) => ({ ...c, filters: [...c.filters, { key: '', value: '' }] }));
  }
  function updateFilter(i: number, patch: Partial<{ key: string; value: string }>) {
    setConfig((c) => ({ ...c, filters: c.filters.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) }));
  }
  function removeFilter(i: number) {
    setConfig((c) => ({ ...c, filters: c.filters.filter((_, idx) => idx !== i) }));
  }

  function handleRun() {
    if (!canRun) return;
    runMutation.mutate(
      { propertyId, ...toRunRequest(config) },
      {
        onSuccess: (data) => { setResult(data); setLastRunOk(true); },
        onError: () => { setLastRunOk(false); },
      },
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-5">
        <Chips label="Dimensions" options={DIMENSIONS} selected={config.dimensions} onToggle={toggleDimension} />
        <Chips label="Measures" options={MEASURES} selected={config.measures} onToggle={toggleMeasure} />

        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div>
            <label htmlFor="rb-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input id="rb-from" type="date" value={config.dateFrom} onChange={(e) => setConfig((c) => ({ ...c, dateFrom: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor="rb-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input id="rb-to" type="date" value={config.dateTo} onChange={(e) => setConfig((c) => ({ ...c, dateTo: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        {/* Filters */}
        <div>
          <span className="block text-sm font-medium text-gray-700 mb-1.5">Filters</span>
          <div className="space-y-2">
            {config.filters.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <select value={f.key} onChange={(e) => updateFilter(i, { key: e.target.value })} className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="">Field…</option>
                  <option value="department">Department</option>
                  <option value="settlement_type">Settlement type</option>
                  <option value="cashier">Cashier</option>
                </select>
                <input type="text" value={f.value} onChange={(e) => updateFilter(i, { value: e.target.value })} placeholder="Value" className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                <button type="button" onClick={() => removeFilter(i)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Remove filter">
                  <X size={16} />
                </button>
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addFilter}>
              <Plus size={14} aria-hidden="true" />
              Add filter
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
          <Button onClick={handleRun} isLoading={runMutation.isPending} disabled={!canRun}>
            <Play size={16} aria-hidden="true" />
            Run
          </Button>
          <Button variant="secondary" onClick={() => setShowSave(true)} disabled={!lastRunOk}>
            <Save size={16} aria-hidden="true" />
            Save report
          </Button>
          {!canRun && (
            <span className="text-xs text-gray-400">Pick at least one dimension and one measure.</span>
          )}
        </div>
      </div>

      {runMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(runMutation.error)}
        </p>
      )}

      {result && (
        <ReportResults config={config} result={result} />
      )}

      <SlideOver isOpen={showSave} onClose={() => setShowSave(false)} title="Save report">
        <SaveReportForm propertyId={propertyId} config={config} onClose={() => setShowSave(false)} />
      </SlideOver>
    </div>
  );
}

// ─── Results ─────────────────────────────────────────────────────────────────

function ReportResults({ config, result }: { config: BuilderConfig; result: ReportRunResponse }) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const rows = result.rows ?? [];
  const sortedRows = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const an = typeof av === 'number' ? av : parseFloat(String(av));
      const bn = typeof bv === 'number' ? bv : parseFloat(String(bv));
      let cmp: number;
      if (Number.isFinite(an) && Number.isFinite(bn)) cmp = an - bn;
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  function toggleSort(key: string) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  }

  function renderCell(value: string | number | undefined, isMoney: boolean) {
    if (value == null) return '--';
    if (isMoney) return formatMoney(value);
    return String(value);
  }

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="flex flex-wrap gap-3" aria-live="polite">
        {config.measures.map((m) => {
          const isMoney = MONEY_MEASURES.has(m);
          const total = result.totals?.[m];
          return (
            <div key={m} className="rounded-xl border border-gray-200 bg-white px-4 py-3 min-w-[8rem]">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{MEASURE_LABEL[m]}</p>
              <p className="mt-1 text-lg font-bold text-gray-900">
                {total == null ? '--' : isMoney ? formatMoney(total) : String(total)}
              </p>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-gray-500">{rows.length} row{rows.length === 1 ? '' : 's'}</p>

      {rows.length === 0 ? (
        <EmptyState icon={BarChart3} title="No data for these criteria" description="Adjust the dimensions, measures, or date range and run again." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  {config.dimensions.map((d) => (
                    <SortableTh key={d} active={sortKey === d} dir={sortDir} onClick={() => toggleSort(d)}>{DIM_LABEL[d]}</SortableTh>
                  ))}
                  {config.measures.map((m) => (
                    <SortableTh key={m} active={sortKey === m} dir={sortDir} onClick={() => toggleSort(m)}>{MEASURE_LABEL[m]}</SortableTh>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {config.dimensions.map((d) => (
                      <td key={d} className="px-4 py-3 text-sm text-gray-700">{renderCell(row[d], false)}</td>
                    ))}
                    {config.measures.map((m) => (
                      <td key={m} className="px-4 py-3 text-sm text-gray-900">{renderCell(row[m], MONEY_MEASURES.has(m))}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableTh({ children, active, dir, onClick }: { children: React.ReactNode; active: boolean; dir: 'asc' | 'desc'; onClick: () => void }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      <button type="button" onClick={onClick} className="inline-flex items-center gap-1 hover:text-gray-700">
        {children}
        {active && <span aria-hidden="true">{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}

// ─── Save form ───────────────────────────────────────────────────────────────

function SaveReportForm({ propertyId, config, onClose }: { propertyId: string; config: BuilderConfig; onClose: () => void }) {
  const [name, setName] = useState('');
  const createMutation = useCreateSavedReport();
  const isValid = name.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    createMutation.mutate(
      { propertyId, name: name.trim(), config: toRunRequest(config) },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="sr-name" className="block text-sm font-medium text-gray-700 mb-1">
          Report name <span className="text-red-500">*</span>
        </label>
        <input id="sr-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); if (createMutation.isError) createMutation.reset(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending} disabled={!isValid}>Save</Button>
      </div>
    </form>
  );
}

// ─── Saved tab ───────────────────────────────────────────────────────────────

function SavedReportsTab({ propertyId, onLoad }: { propertyId: string; onLoad: (def: SavedReportDefinition) => void }) {
  const listQuery = useSavedReports({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteSavedReport();
  const [renaming, setRenaming] = useState<SavedReportDefinition | null>(null);

  const defs: SavedReportDefinition[] = listQuery.data ?? [];

  function handleDelete(def: SavedReportDefinition) {
    if (!window.confirm(`Delete "${def.name}"?`)) return;
    deleteMutation.mutate(def.id, {
      onError: (err) => window.alert(extractErrorMessage(err)),
    });
  }

  function summarize(def: SavedReportDefinition, kind: 'dimensions' | 'measures'): string {
    const c = (def.config ?? {}) as Partial<ReportRunRequest>;
    const list = (kind === 'dimensions' ? c.dimensions : c.measures) ?? [];
    const labels = list.map((k) => (kind === 'dimensions' ? DIM_LABEL[k] : MEASURE_LABEL[k]) ?? k);
    return labels.join(', ') || '--';
  }

  if (listQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }
  if (listQuery.isError) {
    return (
      <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
        {extractErrorMessage(listQuery.error)}
      </p>
    );
  }
  if (defs.length === 0) {
    return <EmptyState icon={Save} title="No saved reports" description="Build one in the Builder tab and save it." />;
  }

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <Th>Name</Th>
                <Th>Dimensions</Th>
                <Th>Measures</Th>
                <Th>Updated</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {defs.map((def) => (
                <tr key={def.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onLoad(def)}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{def.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{summarize(def, 'dimensions')}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{summarize(def, 'measures')}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtTs(def.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={(e) => { e.stopPropagation(); setRenaming(def); }} className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50" aria-label="Rename">
                        <Pencil size={14} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleDelete(def); }} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <SlideOver isOpen={renaming !== null} onClose={() => setRenaming(null)} title="Rename report">
        {renaming && <RenameReportForm def={renaming} onClose={() => setRenaming(null)} />}
      </SlideOver>
    </>
  );
}

function RenameReportForm({ def, onClose }: { def: SavedReportDefinition; onClose: () => void }) {
  const [name, setName] = useState(def.name);
  const updateMutation = useUpdateSavedReport();
  const isValid = name.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    updateMutation.mutate(
      { id: def.id, payload: { name: name.trim() } },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="rr-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input id="rr-name" type="text" required value={name} onChange={(e) => { setName(e.target.value); if (updateMutation.isError) updateMutation.reset(); }} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
      </div>
      {updateMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(updateMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={updateMutation.isPending} disabled={!isValid}>Save</Button>
      </div>
    </form>
  );
}

// ─── Activity log tab ────────────────────────────────────────────────────────

function ActivityLogTab({ propertyId }: { propertyId: string }) {
  const [eventType, setEventType] = useState<RetailActivityType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const logQuery = useActivityLog(
    {
      propertyId,
      eventType: eventType || undefined,
      from: from || undefined,
      to: to || undefined,
      page,
      limit: PAGE_SIZE,
    },
    !!propertyId,
  );

  const rows: ActivityLogRow[] = logQuery.data?.data ?? [];
  const meta = logQuery.data?.meta;
  const totalPages = meta ? Math.max(1, Math.ceil(meta.total / meta.limit)) : 1;

  function resetPageAnd(fn: () => void) { fn(); setPage(1); }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="al-type" className="block text-xs font-medium text-gray-600 mb-1">Event type</label>
          <select id="al-type" value={eventType} onChange={(e) => resetPageAnd(() => setEventType(e.target.value as RetailActivityType | ''))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500">
            <option value="">All</option>
            {Object.values(RetailActivityType).map((t) => (
              <option key={t} value={t}>{ACTIVITY_LABELS[t]}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="al-from" className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input id="al-from" type="date" value={from} onChange={(e) => resetPageAnd(() => setFrom(e.target.value))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <div>
          <label htmlFor="al-to" className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input id="al-to" type="date" value={to} onChange={(e) => resetPageAnd(() => setTo(e.target.value))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
      </div>

      {logQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : logQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(logQuery.error)}
        </p>
      ) : rows.length === 0 ? (
        <EmptyState icon={BarChart3} title="No activity" description="No activity in this range." />
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <Th>When</Th>
                    <Th>Actor</Th>
                    <Th>Type</Th>
                    <Th>Summary</Th>
                    <Th>Amount</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{fmtTs(r.occurredAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r.actor ?? '--'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                          {ACTIVITY_LABELS[r.type] ?? r.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{r.summary}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.amount != null ? formatMoney(r.amount) : '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}{meta ? ` · ${meta.total} total` : ''}</p>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
                <ChevronLeft size={14} aria-hidden="true" />
                Prev
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                Next
                <ChevronRight size={14} aria-hidden="true" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
